import * as React from 'react';
import { Check } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { RatingChip } from '@/components/ui/rating-chip';
import { WorkspaceButton, isTypingTarget } from '@/components/ui/workspace';
import type { VendorIntelligence, VendorRating } from '@/lib/api/vendor-intelligence';
import { cn } from '@/lib/utils/cn';
import {
  cellKey,
  fitLine,
  judgeReasonText,
  judgeRunsByCell,
  moduleMeta,
  proposedIdsOf,
  ratingsByCell,
} from './vi-logic';

/**
 * The ratings grid (T-0267; E14-design §3): the evaluated modules down the
 * side with their weight and requirement counts, the products in scope
 * across, a rating chip in each cell (outlined while proposed, filled once
 * confirmed). A proposed cell carries a tick that confirms it in place;
 * clicking the cell opens its panel (the evidence, or a change with a
 * reason). Each column can confirm all its proposed ratings at once, and the
 * step's toolbar confirms every proposed rating in the grid.
 *
 * The grid fills its step and scrolls itself (T-0272): the header row and
 * the module column stay put while the cells scroll, and a vendor's rank
 * and index sit under its name once confirmed ratings rank it.
 *
 * Keyboard: the arrow keys move between cells, Enter opens one, `c`
 * confirms the focused rating when it is proposed.
 */

export interface GridCell {
  productId: string;
  moduleId: string;
}

export function RatingsGrid({
  evaluation,
  waiting,
  selected,
  busy,
  onOpen,
  onConfirm,
  onConfirmAll,
  className,
}: {
  evaluation: VendorIntelligence;
  /** Cells the judge is still working on (keys from `cellKey`). */
  waiting: ReadonlySet<string>;
  selected: GridCell | null;
  busy: boolean;
  onOpen: (cell: GridCell) => void;
  onConfirm: (rating: VendorRating) => void;
  onConfirmAll: (productId: string, ratingIds: string[]) => void;
  className?: string;
}) {
  const { products, modules, can_edit: canEdit } = evaluation;
  const ratings = React.useMemo(() => ratingsByCell(evaluation), [evaluation]);
  const runs = React.useMemo(() => judgeRunsByCell(evaluation), [evaluation]);
  const fitOf = React.useMemo(
    () => new Map(evaluation.fit.products.map((p) => [p.product_id, p])),
    [evaluation.fit.products],
  );
  const [focus, setFocus] = React.useState<{ row: number; col: number }>({ row: 0, col: 0 });
  const refs = React.useRef(new Map<string, HTMLButtonElement>());

  const row = Math.min(focus.row, Math.max(modules.length - 1, 0));
  const col = Math.min(focus.col, Math.max(products.length - 1, 0));

  // A cell selected from outside the grid (a link from the area matrix,
  // T-0276) scrolls into view; focus stays where the reader is.
  React.useEffect(() => {
    if (!selected) return;
    refs.current
      .get(cellKey(selected.productId, selected.moduleId))
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [selected]);

  const moveTo = (r: number, c: number) => {
    const nr = Math.max(0, Math.min(modules.length - 1, r));
    const nc = Math.max(0, Math.min(products.length - 1, c));
    setFocus({ row: nr, col: nc });
    const m = modules[nr];
    const p = products[nc];
    if (m && p) refs.current.get(cellKey(p.product_id, m.module_id))?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (isTypingTarget(e.target)) return;
    const m = modules[row];
    const p = products[col];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveTo(row + 1, col);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveTo(row - 1, col);
        break;
      case 'ArrowRight':
        e.preventDefault();
        moveTo(row, col + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moveTo(row, col - 1);
        break;
      case 'Enter':
        if (m && p) {
          e.preventDefault();
          onOpen({ productId: p.product_id, moduleId: m.module_id });
        }
        break;
      case 'c': {
        if (!m || !p || !canEdit || busy) break;
        const r = ratings.get(cellKey(p.product_id, m.module_id));
        if (r?.status === 'proposed') {
          e.preventDefault();
          onConfirm(r);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={cn('min-h-0 overflow-auto rounded-card border border-ink-100', className)}>
      <table
        role="grid"
        aria-label="Ratings by module and vendor"
        className="w-full border-separate border-spacing-0 text-body-sm"
        onKeyDown={onKeyDown}
      >
        <thead>
          <tr className="align-bottom">
            <th
              scope="col"
              className="sticky left-0 top-0 z-30 min-w-[240px] border-b border-ink-100 bg-paper-0 px-3 py-2 text-left text-meta font-medium text-ink-500"
            >
              Module
            </th>
            {products.map((p) => {
              const proposed = proposedIdsOf(evaluation, p.product_id);
              const fit = fitOf.get(p.product_id);
              const line = fit ? fitLine(fit) : null;
              return (
                <th
                  key={p.product_id}
                  scope="col"
                  className="sticky top-0 z-20 min-w-[150px] border-b border-ink-100 bg-paper-0 px-3 py-2 text-left font-normal"
                >
                  <div className="text-meta text-ink-500">{p.vendor.name}</div>
                  <div className="font-semibold text-ink-900">{p.name}</div>
                  {line ? (
                    <div
                      className="font-mono text-meta tabular-nums text-ink-700"
                      title="Rank and fit index, from confirmed ratings"
                    >
                      {line}
                    </div>
                  ) : null}
                  {canEdit && proposed.length > 0 ? (
                    <WorkspaceButton
                      variant="tertiary"
                      className="-ml-2.5 mt-1"
                      disabled={busy}
                      aria-label={`Confirm all ${proposed.length} proposed for ${p.name}`}
                      onClick={() => onConfirmAll(p.product_id, proposed)}
                    >
                      Confirm all proposed ({proposed.length})
                    </WorkspaceButton>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {modules.map((m, r) => (
            <tr key={m.module_id}>
              <th
                scope="row"
                className="sticky left-0 z-10 border-b border-ink-100 bg-paper-0 px-3 py-2 text-left align-top font-normal"
              >
                <div className="font-medium text-ink-900">{m.name}</div>
                <div className="text-meta text-ink-500">{moduleMeta(m)}</div>
              </th>
              {products.map((p, c) => {
                const key = cellKey(p.product_id, m.module_id);
                const rating = ratings.get(key) ?? null;
                const run = runs.get(key) ?? null;
                const isWaiting = waiting.has(key);
                const isSelected =
                  selected?.productId === p.product_id && selected?.moduleId === m.module_id;
                const notProposed =
                  !rating && run !== null && (run.status === 'skipped' || run.status === 'failed');
                const quickConfirm =
                  canEdit && rating?.status === 'proposed' && !isWaiting ? rating : null;
                return (
                  <td
                    key={p.product_id}
                    role="gridcell"
                    className="border-b border-ink-100 px-2 py-1.5 align-top"
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) refs.current.set(key, el);
                          else refs.current.delete(key);
                        }}
                        tabIndex={r === row && c === col ? 0 : -1}
                        aria-label={`${p.name}, ${m.name}: ${
                          isWaiting
                            ? 'proposing'
                            : rating
                              ? `${rating.rating}, ${rating.status}`
                              : notProposed
                                ? 'not proposed'
                                : 'not rated'
                        }`}
                        aria-pressed={isSelected}
                        title={notProposed ? judgeReasonText(run!.reason) : undefined}
                        onFocus={() => setFocus({ row: r, col: c })}
                        onClick={() => {
                          setFocus({ row: r, col: c });
                          onOpen({ productId: p.product_id, moduleId: m.module_id });
                        }}
                        className={cn(
                          // `relative` contains any screen-reader text (chip, spinner).
                          'relative flex min-h-[30px] min-w-0 flex-1 items-center gap-1.5 rounded-chip px-1.5 text-left hover:bg-paper-50',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                          isSelected && 'bg-paper-100',
                        )}
                      >
                        {rating ? (
                          <RatingChip
                            rating={rating.rating}
                            state={rating.status === 'confirmed' ? 'confirmed' : 'proposed'}
                            size="sm"
                          />
                        ) : isWaiting ? null : (
                          <span className="text-meta text-ink-300">
                            {notProposed ? 'Not proposed' : 'Not rated'}
                          </span>
                        )}
                        {isWaiting ? (
                          <>
                            <Spinner className="h-3.5 w-3.5" />
                            {rating ? null : (
                              <span className="text-meta text-ink-500">Proposing</span>
                            )}
                          </>
                        ) : null}
                      </button>
                      {quickConfirm ? (
                        <button
                          type="button"
                          // The grid's keyboard model confirms with `c`; this is the pointer's way.
                          tabIndex={-1}
                          disabled={busy}
                          aria-label={`Confirm ${quickConfirm.rating} rating of ${p.name} for ${m.name}`}
                          title="Confirm this rating"
                          onClick={() => onConfirm(quickConfirm)}
                          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-chip text-ink-500 hover:bg-paper-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
